import { ax } from '@/services/_ax';
import { convertDateTimeToDate } from '@/utils';
import moment from 'moment';

/**
 * Get upcoming appointments (next week) for user by id (all users)
 * @param {string} uid - uid for desired user
 */
const getUpcoming = async (uid) => {
  const start = moment().format();

  const { data } = await ax.get(
    `/sessions?user_id=${uid}&session_start_after=${start}`,
  );

  return data;
};

/**
 * Set availability for user by id (provider only)
 * @param {string} providerId current provider id
 * @param {string} platformId current platform id
 * @param {date} start start date for new unavailability
 * @param {date} end end datetime for new unavailability
 * @returns Object { type: 'unavail' || 'appt', start: new Date(), end: new Date(), data: {} }
 */
const setAvailability = async (providerId, platformId, start, end) => {
  const payload = {
    platform_id: platformId,
    start_datetime: start,
    end_datetime: end,
    title: 'unavailable',
  };
  const { data } = await ax.post(
    `/provider_unavailability/${providerId}`,
    payload,
  );

  return {
    title: 'Available',
    type: 'unavail',
    start: new Date(data.start_datetime),
    end: new Date(data.end_datetime),
    data: { ...data },
  };
};

const removeUnavailability = async (providerId, unavailId) => {
  const { data } = await ax.delete(
    `/provider_unavailability/${providerId}/${unavailId}`,
  );
  return data;
};

/**
 * Block out a span of time from a provider's bookable availability.
 *
 * IMPORTANT — data model: a `user_unavailability` row is actually a BOOKABLE
 * window (the backend chops each row into 15-min patient-bookable slots).
 * "Blocking out" therefore means carving the requested span OUT of any window
 * that overlaps it: shrink it, split it in two, or delete it entirely.
 *
 * Safety: we CREATE the remaining (still-bookable) pieces BEFORE deleting the
 * original window. If anything fails mid-way the provider keeps too much
 * availability rather than losing it — we never silently zero out a provider's
 * bookable time (which would mean no appointments / no revenue).
 *
 * @param {string} providerId
 * @param {string} platformId
 * @param {string} timezone provider timezone (used only to fetch surrounding windows)
 * @param {string} blockStart ISO 8601 UTC start of the span to block out
 * @param {string} blockEnd ISO 8601 UTC end of the span to block out
 * @returns {Promise<{ windowsAffected: number, appointmentConflicts: number }>}
 */
const blockOutTime = async (
  providerId,
  platformId,
  timezone,
  blockStart,
  blockEnd,
) => {
  const bStart = moment.utc(blockStart);
  const bEnd = moment.utc(blockEnd);

  const bookings = await getBookings(providerId, timezone, {
    start_time: bStart.clone().subtract(1, 'day').toISOString(),
    end_time: bEnd.clone().add(1, 'day').toISOString(),
  });

  const overlaps = (start, end) =>
    moment.utc(start).isBefore(bEnd) && moment.utc(end).isAfter(bStart);

  const overlappingWindows = bookings.filter(
    (item) =>
      item.type === 'unavail' &&
      item.data &&
      item.data.uid &&
      overlaps(item.data.start_datetime, item.data.end_datetime),
  );

  const appointmentConflicts = bookings.filter(
    (item) =>
      item.type === 'appt' &&
      item.data &&
      overlaps(
        item.data.appointment_start_time,
        item.data.appointment_end_time,
      ),
  ).length;

  for (const window of overlappingWindows) {
    const wStart = moment.utc(window.data.start_datetime);
    const wEnd = moment.utc(window.data.end_datetime);
    const hasLeftRemainder = wStart.isBefore(bStart);
    const hasRightRemainder = wEnd.isAfter(bEnd);

    // 1) Re-create the parts of this window that stay bookable FIRST.
    if (hasLeftRemainder) {
      await setAvailability(
        providerId,
        platformId,
        wStart.toISOString(),
        bStart.toISOString(),
      );
    }
    if (hasRightRemainder) {
      await setAvailability(
        providerId,
        platformId,
        bEnd.toISOString(),
        wEnd.toISOString(),
      );
    }

    // 2) Only now remove the original window covering the blocked span.
    await removeUnavailability(providerId, window.data.uid);
  }

  return {
    windowsAffected: overlappingWindows.length,
    appointmentConflicts,
  };
};

/**
 * Get appointments and unavailability for user by id (provider only)
 * @param {string} uid - provider uid
 * @returns { appointments: Array, unavailability: Array }
 */
const getBookings = async (uid, timezone, params) => {
  const {
    data: { appointments, unavailability },
  } = await ax.get(`/provider_unavailability/${uid}`, {
    params,
  });
  const res = [];

  appointments.forEach((item) => {
    const newitem = {
      type: 'appt',
      title: 'Booked appointment',
      start: convertDateTimeToDate(item.appointment_start_time, timezone),
      end: convertDateTimeToDate(item.appointment_end_time, timezone),
      data: { ...item },
    };
    res.push(newitem);
  });

  // A `user_unavailability` row is actually a BOOKABLE window (the backend
  // turns each row into patient-bookable slots), so we surface it as "Available".
  unavailability.forEach((item) => {
    const newitem = {
      type: 'unavail',
      title: 'Available',
      start: convertDateTimeToDate(item.start_datetime, timezone),
      end: convertDateTimeToDate(item.end_datetime, timezone),
      data: { ...item },
    };
    res.push(newitem);
  });

  return res;
};

/**
 * Get appointments and unavailability for user by id (provider only)
 * @param {string} uid - provider uid
 * @returns { appointments: Array, unavailability: Array }
 */
const getBookingsByState = async (state, timezone, params) => {
  const {
    data: { unavailability },
  } = await ax.get(`/provider_unavailability_by_state/${state}`, {
    params,
  });
  const res = [];

  // Render EVERY provider's availability window as its own event. Previously
  // these were merged into a single union of overlapping intervals, which
  // collapsed multiple providers into one block and dropped `user_id` — so any
  // provider whose hours overlapped another's effectively disappeared from the
  // admin calendar. Keeping each row distinct shows ALL providers' availability.
  unavailability.forEach((item) => {
    res.push({
      type: 'unavail',
      title: 'Available',
      start: convertDateTimeToDate(item.start_datetime, timezone),
      end: convertDateTimeToDate(item.end_datetime, timezone),
      data: { ...item },
    });
  });

  return res;
};

const calendarService = {
  getUpcoming,
  getBookings,
  getBookingsByState,
  setAvailability,
  removeUnavailability,
  blockOutTime,
};
export default calendarService;
